import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { BrandAliasInput, BrandInput } from "@reina/shared";
import type { Brand } from "@reina/shared";
import {
  Button,
  EmptyState,
  Field,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  TableWrap,
  useToast,
} from "../../components";
import {
  useAddBrandAlias,
  useBrands,
  useCreateBrand,
  useRemoveBrandAlias,
  useUpdateBrand,
} from "../../api/catalogue";
import { errorMessage, formatDate } from "../../lib/format";

export function BrandsPage() {
  const brands = useBrands();
  const items = brands.data?.items ?? [];
  const [modal, setModal] = useState<{ brand: Brand | null } | null>(null);

  // Keep the modal's brand fresh after alias mutations refetch the list.
  const modalBrand =
    modal?.brand == null ? modal?.brand ?? null : items.find((b) => b.id === modal.brand?.id) ?? modal.brand;

  const newButton = (
    <Button variant="primary" onClick={() => setModal({ brand: null })}>
      New brand
    </Button>
  );

  return (
    <>
      <Panel>
        <PanelHead
          title="Brands"
          sub={
            brands.data
              ? `${brands.data.total} brand${brands.data.total === 1 ? "" : "s"} + aliases`
              : "brand names + spelling aliases"
          }
          actions={newButton}
        />
        {brands.isPending ? (
          <PanelBody>
            <EmptyState title="Loading brands…" />
          </PanelBody>
        ) : brands.isError ? (
          <PanelBody>
            <EmptyState title="Could not load brands" hint={errorMessage(brands.error)} />
          </PanelBody>
        ) : items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title="No brands yet"
              hint="e.g. OSMA with alias OSMAWELD — aliases fold spellings during parsing."
              action={newButton}
            />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Brand</th>
                <th>Aliases</th>
                <th>Updated</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((brand) => (
                <tr key={brand.id} onClick={() => setModal({ brand })} style={{ cursor: "pointer" }}>
                  <td>
                    <strong>{brand.name}</strong>
                  </td>
                  <td>
                    {brand.aliases && brand.aliases.length > 0 ? (
                      <span className="actions" style={{ justifyContent: "flex-start" }}>
                        {brand.aliases.map((alias) => (
                          <Pill key={alias.id} tone="neutral">
                            {alias.alias}
                          </Pill>
                        ))}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="mono">{formatDate(brand.updatedAt)}</td>
                  <td>
                    <Button
                      mini
                      onClick={(event) => {
                        event.stopPropagation();
                        setModal({ brand });
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>
      {modal && (
        <BrandModal
          key={modal.brand?.id ?? "new"}
          brand={modalBrand}
          onCreated={(created) => setModal({ brand: created })}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Create/edit modal — aliases editable once the brand exists
// ---------------------------------------------------------------------------

function BrandModal({
  brand,
  onCreated,
  onClose,
}: {
  brand: Brand | null;
  onCreated: (brand: Brand) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateBrand();
  const update = useUpdateBrand();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof BrandInput>, unknown, z.output<typeof BrandInput>>({
    resolver: zodResolver(BrandInput),
    defaultValues: { name: brand?.name ?? "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (brand) {
        await update.mutateAsync({ id: brand.id, input: values });
        toast.push({ message: "Brand updated", tone: "ok" });
        onClose();
      } else {
        const created = await create.mutateAsync(values);
        toast.push({ message: "Brand created — add aliases below", tone: "ok" });
        onCreated(created);
      }
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  });

  return (
    <Modal
      open
      title={brand ? "Edit brand" : "New brand"}
      sub={brand ? brand.name : "add a manufacturer brand"}
      onClose={onClose}
      footer={
        <>
          <span className="sub">
            {brand ? "Aliases fold alternate spellings during parsing." : "Save first to add aliases."}
          </span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              {brand ? "Close" : "Cancel"}
            </Button>
            <Button variant="primary" type="submit" form="brand-form" disabled={isSubmitting}>
              {brand ? "Save changes" : "Create brand"}
            </Button>
          </div>
        </>
      }
    >
      <form id="brand-form" className="stack" onSubmit={onSubmit} noValidate>
        <Field label="Name" hint="Canonical name, e.g. OSMA" error={errors.name?.message}>
          <input className="field" autoFocus {...register("name")} />
        </Field>
      </form>
      {brand && <AliasSection brand={brand} />}
    </Modal>
  );
}

function AliasSection({ brand }: { brand: Brand }) {
  const toast = useToast();
  const addAlias = useAddBrandAlias();
  const removeAlias = useRemoveBrandAlias();
  const aliases = brand.aliases ?? [];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof BrandAliasInput>, unknown, z.output<typeof BrandAliasInput>>({
    resolver: zodResolver(BrandAliasInput),
    defaultValues: { alias: "" },
  });

  const onAdd = handleSubmit(async (values) => {
    try {
      await addAlias.mutateAsync({ brandId: brand.id, input: values });
      toast.push({ message: "Alias added", tone: "ok" });
      reset({ alias: "" });
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  });

  const onRemove = async (aliasId: string) => {
    try {
      await removeAlias.mutateAsync({ brandId: brand.id, aliasId });
      toast.push({ message: "Alias removed", tone: "ok" });
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <section className="stack">
      <span className="label">Aliases</span>
      {aliases.length === 0 ? (
        <EmptyState title="No aliases" hint="e.g. OSMAWELD → OSMA" />
      ) : (
        <div className="line-list">
          {aliases.map((alias) => (
            <div key={alias.id} className="line-row">
              <strong className="mono">{alias.alias}</strong>
              <Button
                mini
                variant="danger"
                disabled={removeAlias.isPending}
                onClick={() => void onRemove(alias.id)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={onAdd} noValidate>
        <Field label="Add alias" error={errors.alias?.message}>
          <span className="actions" style={{ justifyContent: "flex-start", flexWrap: "nowrap" }}>
            <input className="field" placeholder="e.g. OSMAWELD" {...register("alias")} />
            <Button mini variant="primary" type="submit" disabled={isSubmitting}>
              Add
            </Button>
          </span>
        </Field>
      </form>
    </section>
  );
}
